import { memo, useEffect, useRef } from "react";
import { Card, Typography } from "antd";
import { motion } from "motion/react";
import type { SlideComponentProps } from "./SlideRenderer";
import { StreamingText } from "./StreamingText";
import { ANIMATION } from "./animationConfig";
import { useAnimationSequence } from "./useAnimationSequence";
import { DoubleParagraphPlaceholders } from "types/";

export const DoubleParagraphSlide = memo(
  ({ slide, isActive = false, onComplete }: SlideComponentProps) => {
    const placeholders = slide.placeholders as DoubleParagraphPlaceholders;
    const title = placeholders.title;
    const paragraph1 = placeholders.paragraph_1;
    const paragraph2 = placeholders.paragraph_2;
    const hasCalledComplete = useRef(false);

    // Steps: 0=title, 1=paragraph1, 2=paragraph2
    const totalSteps = 3;
    const { nextStep, isStepActive, isComplete } = useAnimationSequence(
      totalSteps,
      isActive
    );

    // Notify parent when complete (once)
    useEffect(() => {
      if (isComplete && onComplete && !hasCalledComplete.current) {
        hasCalledComplete.current = true;
        onComplete();
      }
    }, [isComplete, onComplete]);

    // Not active yet - render static content
    if (!isActive) {
      return (
        <Card>
          <Typography.Title level={4}>{title}</Typography.Title>
          <Typography.Paragraph style={{ marginBottom: 16 }}>
            {paragraph1}
          </Typography.Paragraph>
          <Typography.Paragraph style={{ marginBottom: 0 }}>
            {paragraph2}
          </Typography.Paragraph>
        </Card>
      );
    }

    return (
      <Card>
        {/* Step 0: Title */}
        {isStepActive(0) && (
          <Typography.Title level={4}>
            <StreamingText
              text={title}
              animate
              wordDelay={ANIMATION.WORD_DELAY}
              onComplete={nextStep}
            />
          </Typography.Title>
        )}

        {/* Step 1: Paragraph 1 */}
        {isStepActive(1) && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            <Typography.Paragraph style={{ marginBottom: 16 }}>
              <StreamingText
                text={paragraph1}
                animate
                wordDelay={ANIMATION.WORD_DELAY}
                onComplete={nextStep}
              />
            </Typography.Paragraph>
          </motion.div>
        )}

        {/* Step 2: Paragraph 2 */}
        {isStepActive(2) && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            <Typography.Paragraph style={{ marginBottom: 0 }}>
              <StreamingText
                text={paragraph2}
                animate
                wordDelay={ANIMATION.WORD_DELAY}
                onComplete={nextStep}
              />
            </Typography.Paragraph>
          </motion.div>
        )}
      </Card>
    );
  }
);

DoubleParagraphSlide.displayName = "DoubleParagraphSlide";
